<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  PRIORITIES,
  PRIORITY_LABELS,
  PRIORITY_TAG,
  SOURCES,
  SOURCE_LABELS,
  STATUS_LABELS,
  STATUS_TAG,
  canAct,
  closeDemand,
  createDemand,
  demandInput,
  getDemand,
  listDemands,
  planDemand,
  respondDemand,
  updateDemand,
  type Demand,
  type DemandForm,
  type DemandRow,
  type DemandStatus,
} from '../api/demands'
import { toPickerValue } from '../api/datetime'

const rows = ref<DemandRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const query = reactive({ keyword: '', status: '', priority: '', source: '', page: 1, size: 20 })

/** 新建 / 编辑 */
const formOpen = ref(false)
const editing = ref<DemandRow | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const loadingDetail = ref(false)
const form = reactive<DemandForm>({
  item_name: '',
  qty: 100,
  region: '',
  source: 'manual',
  priority: 'normal',
  forecast_at: null,
  remark: '',
})

/** 状态流转的失败（非法流转是 409）与加载失败分开显示，都带 trace_id */
const actionError = ref<unknown>(null)
const acting = ref(false)

const itemError = computed(() => (form.item_name.trim() ? '' : '请填写物资名称'))

const qtyError = computed(() => (Number(form.qty) > 0 ? '' : '数量必须大于 0'))

const regionError = computed(() => (form.region.trim() ? '' : '请填写区域'))

const formErrorText = computed(() => itemError.value || qtyError.value || regionError.value)

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listDemands({
      page: query.page,
      size: query.size,
      keyword: query.keyword,
      status: query.status,
      priority: query.priority,
      source: query.source,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => void load())

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function resetForm(): void {
  Object.assign(form, {
    item_name: '',
    qty: 100,
    region: '',
    source: 'manual' as DemandForm['source'],
    priority: 'normal' as DemandForm['priority'],
    forecast_at: null,
    remark: '',
  })
}

function openCreate(): void {
  editing.value = null
  resetForm()
  formError.value = null
  formOpen.value = true
}

async function openEdit(row: DemandRow): Promise<void> {
  formError.value = null
  loadingDetail.value = true
  try {
    const d = await getDemand(row.id)
    editing.value = row
    Object.assign(form, {
      item_name: d.item_name,
      qty: d.qty,
      region: d.region,
      source: d.source,
      priority: d.priority,
      forecast_at: toPickerValue(d.forecast_at),
      remark: d.remark,
    })
    formOpen.value = true
  } catch (e) {
    error.value = e
  } finally {
    loadingDetail.value = false
  }
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  const input = demandInput(form)
  try {
    // 单号与状态都由服务端处理：新建落 new，编辑只改字段（后端 Update 会挡已关闭的需求）
    const saved = editing.value
      ? await updateDemand(editing.value.id, input)
      : await createDemand(input)
    formOpen.value = false
    await load(`需求 ${saved.doc_no} 已${editing.value ? '更新' : '创建'}`)
  } catch (e) {
    // 数量为负、来源/优先级非法等都是后端 422，原样把 trace_id 露出来
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function act(row: Demand, action: 'plan' | 'respond' | 'close', verb: string): Promise<void> {
  actionError.value = null
  acting.value = true
  try {
    const saved =
      action === 'plan'
        ? await planDemand(row.id)
        : action === 'respond'
          ? await respondDemand(row.id)
          : await closeDemand(row.id)
    await load(`需求 ${saved.doc_no} ${verb}`)
  } catch (e) {
    // 非法流转（重复规划、状态不对）后端 409，把 trace_id 露出来
    actionError.value = e
  } finally {
    acting.value = false
  }
}
</script>

<template>
  <div class="yc-demand">
    <header class="yc-demand__bar">
      <el-input
        v-model="query.keyword"
        class="yc-demand__keyword"
        placeholder="单号 / 物资名称"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select
        v-model="query.status"
        class="yc-demand__ref"
        placeholder="全部状态"
        clearable
        @change="search"
      >
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-select
        v-model="query.priority"
        class="yc-demand__ref"
        placeholder="全部优先级"
        clearable
        @change="search"
      >
        <el-option v-for="p in PRIORITIES" :key="p" :label="PRIORITY_LABELS[p]" :value="p" />
      </el-select>
      <el-select
        v-model="query.source"
        class="yc-demand__ref"
        placeholder="全部来源"
        clearable
        @change="search"
      >
        <el-option v-for="s in SOURCES" :key="s" :label="SOURCE_LABELS[s]" :value="s" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'scr:demand:create'" type="primary" @click="openCreate">新建需求</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-demand__notice">{{ notice }}</span>
    </header>

    <div v-if="error || actionError" class="yc-demand__error">
      <TraceIdAlert :error="error ?? actionError" />
    </div>

    <el-card v-loading="loading" class="yc-demand__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="doc_no" label="需求单号" min-width="140" />
        <el-table-column label="物资" min-width="150" show-overflow-tooltip>
          <template #default="{ row }">{{ row.item_name }}</template>
        </el-table-column>
        <el-table-column label="数量" width="90" align="right">
          <template #default="{ row }">{{ row.qty }}</template>
        </el-table-column>
        <el-table-column label="区域" width="120" show-overflow-tooltip>
          <template #default="{ row }">{{ row.region || '—' }}</template>
        </el-table-column>
        <el-table-column label="来源" width="110">
          <template #default="{ row }">
            {{ SOURCE_LABELS[row.source as Demand['source']] ?? row.source }}
          </template>
        </el-table-column>
        <el-table-column label="预计需求时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.forecast_at) }}</template>
        </el-table-column>
        <el-table-column label="优先级" width="90">
          <template #default="{ row }">
            <el-tag :type="PRIORITY_TAG[row.priority as Demand['priority']] ?? 'info'" size="small">
              {{ PRIORITY_LABELS[row.priority as Demand['priority']] ?? row.priority }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as DemandStatus] ?? 'info'" size="small">
              {{ STATUS_LABELS[row.status as DemandStatus] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="230" fixed="right">
          <template #default="{ row }">
            <el-button
              v-if="canAct(row.status, 'edit')"
              v-perm="'scr:demand:update'"
              link
              type="primary"
              :disabled="acting"
              @click="openEdit(row)"
            >
              编辑
            </el-button>
            <el-button
              v-if="canAct(row.status, 'plan')"
              v-perm="'scr:demand:update'"
              link
              type="primary"
              :disabled="acting"
              @click="act(row, 'plan', '已规划')"
            >
              规划
            </el-button>
            <el-button
              v-if="canAct(row.status, 'respond')"
              v-perm="'scr:demand:update'"
              link
              type="primary"
              :disabled="acting"
              @click="act(row, 'respond', '已标记为已响应')"
            >
              标记已响应
            </el-button>
            <el-popconfirm
              v-if="canAct(row.status, 'close')"
              title="确认关闭该需求？关闭后不可编辑。"
              width="260"
              @confirm="act(row, 'close', '已关闭')"
            >
              <template #reference>
                <el-button v-perm="'scr:demand:close'" link type="danger" :disabled="acting">关闭</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有需求"
        description="需求信号来自预测、订单或人工登记：先建单，再规划、标记已响应，最后关闭。"
      >
        <el-button v-perm="'scr:demand:create'" type="primary" @click="openCreate">新建需求</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-demand__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <!-- 新建 / 编辑需求 -->
    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑需求 · ${editing.doc_no}` : '新建需求'"
      width="560px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-demand__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <el-form-item label="物资名称" :error="itemError">
          <el-input v-model="form.item_name" maxlength="255" placeholder="如 冷冻鸡胸肉" />
        </el-form-item>

        <el-form-item label="数量" :error="qtyError">
          <el-input-number v-model="form.qty" :min="0" :max="99999999" controls-position="right" />
        </el-form-item>

        <el-form-item label="区域" :error="regionError">
          <el-input v-model="form.region" maxlength="64" placeholder="如 华东 / 成都" />
        </el-form-item>

        <el-form-item label="来源">
          <el-radio-group v-model="form.source">
            <el-radio v-for="s in SOURCES" :key="s" :value="s">{{ SOURCE_LABELS[s] }}</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="优先级">
          <el-radio-group v-model="form.priority">
            <el-radio v-for="p in PRIORITIES" :key="p" :value="p">{{ PRIORITY_LABELS[p] }}</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="预计需求时间">
          <el-date-picker
            v-model="form.forecast_at"
            type="datetime"
            value-format="YYYY-MM-DD HH:mm:ss"
            placeholder="可留空"
          />
        </el-form-item>

        <el-form-item label="备注">
          <el-input v-model="form.remark" maxlength="255" placeholder="可留空" />
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          {{ editing ? '保存' : '创建' }}
        </el-button>
        <span v-if="formErrorText" class="yc-demand__hint yc-demand__hint--inline">{{ formErrorText }}</span>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>

    <p v-if="loadingDetail" class="yc-demand__hint yc-demand__loading">正在加载需求详情…</p>
  </div>
</template>

<style scoped lang="scss">
.yc-demand {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 200px;
  }

  &__ref {
    width: 130px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__hint {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.7;

    &--inline {
      margin-left: var(--yc-space-3);
    }
  }

  &__loading {
    margin-top: var(--yc-space-3);
  }
}
</style>
